import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import useStore from 'state/state';
import ReactHtmlParser from 'react-html-parser';
import GTMTracker from 'tracking/gtm-tracker';
import { shape, bool, number } from 'prop-types';
import Scrollbars from 'components/Scrollbars/Scrollbars';
import StandardButton from 'components/StandardButton/StandardButton';
import styles from './ProductInfo.module.scss';

const ProductInfo = ({ data, descriptionExpanded, type, animateOut }) => {
  const [clampOn, setClampOn] = useState(false);
  const clampOnRef = useRef(false);
  const appData = useStore((state) => state.appData);
  const isOffline = useStore((state) => state.isOffline);
  const [overflowing, setOverflowing] = useState(false);
  const contentRef = useRef();
  const descriptionRef = useRef();

  const onResize = () => {
    if (clampOnRef.current) {
      if (
        descriptionRef.current.clientHeight > contentRef.current.clientHeight
      ) {
        setOverflowing(true);
      } else {
        setOverflowing(false);
      }
    }
  };

  const openProduct = () => {
    GTMTracker.push('HotspotExternalLink', data.alias);
    window.open(data.url, '_blank');
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (clampOn) {
      setTimeout(() => {
        if (
          descriptionRef.current.clientHeight > contentRef.current.clientHeight
        ) {
          setOverflowing(true);
        } else {
          setOverflowing(false);
        }
      }, 300);
    } else {
      setOverflowing(false);
    }
  }, [clampOn]);

  let findOutMoreText = '';
  let productButtonLabel = '';
  if (type === 0) {
    // Product
    findOutMoreText = appData.mainData.infoBoxProductDetail.descriptionText;
    productButtonLabel =
      appData.mainData.infoBoxProductDetail.gotoProductButtonLabel;
  } else {
    // Area
    findOutMoreText = appData.mainData.infoBoxAreaDetail.descriptionText;
    productButtonLabel = appData.mainData.infoBoxAreaDetail.gotoAreaButtonLabel;
  }

  useEffect(() => {
    if (descriptionExpanded) {
      clampOnRef.current = true;
      setClampOn(true);
    } else {
      setTimeout(() => {
        clampOnRef.current = false;
        setClampOn(false);
      }, 300);
    }
  }, [descriptionExpanded]);

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.wrapperExpanded]: descriptionExpanded,
        [styles.fadeOut]: animateOut,
      })}
    >
      <div
        ref={contentRef}
        className={clsx(styles.descriptionContainer, {
          [styles.expanded]: descriptionExpanded,
        })}
      >
        {overflowing ? (
          <Scrollbars>
            <p
              ref={descriptionRef}
              className={clsx(styles.description, {
                [styles.clampOn]: clampOn,
              })}
            >
              {ReactHtmlParser(data.descriptionText)}
            </p>
          </Scrollbars>
        ) : (
          <div
            ref={descriptionRef}
            className={clsx(styles.description, {
              [styles.clampOn]: clampOn,
            })}
          >
            {ReactHtmlParser(data.descriptionText)}
          </div>
        )}
      </div>
      {!isOffline && (
        <div
          className={clsx(styles.bottom, {
            [styles.bottomExpanded]: descriptionExpanded,
          })}
        >
          <p className={styles.toProductDescription}>
            <b>{findOutMoreText}</b>
          </p>

          <StandardButton
            label={productButtonLabel}
            icon="newWindow"
            onClick={openProduct}
            primaryFontColor
            className={descriptionExpanded ? '' : styles.notVisible}
          />
          <span className={styles.dimmedText}>
            {appData.mainData.overallApp.openInNewWindowText}
          </span>
        </div>
      )}
    </div>
  );
};

ProductInfo.propTypes = {
  data: shape({}).isRequired,
  descriptionExpanded: bool.isRequired,
  type: number.isRequired,
  animateOut: bool.isRequired,
};

export default ProductInfo;
